import React, { CSSProperties, useContext, useEffect, useRef } from 'react';
import { Paper } from '@mui/material';
import { hiJackColors } from 'constants/colorConstants';
import ScaleContext from 'contexts/ScaleContext';
import gsap from 'gsap';

import {
  CARD_BACK_ALT,
  CARD_BACK_SRC,
  cardAltText,
  cardCornerRadiusForWidth,
  cardFaceSrc,
  cardHeightForWidth
} from './deck';
import { Card } from '.';

type CardPropType = Card & {
  isFaceDown?: boolean;
  elevation?: number;
  enableScale?: boolean;
  isEmptyCard?: boolean;
  width?: number;
  /**
   * @deprecated The deck artwork fixes the card's shape — height follows
   * `width` (see deck.ts CARD_ASPECT_RATIO). Kept so existing callers compile.
   */
  height?: number;
  /** @deprecated The rank is part of the artwork now; nothing is typeset. */
  fontSize?: number;
};

/**
 * The mini card — winner banner and strip. Same two-phase flip as SmallCard.
 *
 * Face and back are single artworks from the deck (see ./deck.ts); this
 * component only sizes and flips them. An empty card is the dashed outline of
 * exactly the box a card would fill.
 */
const XSmallCard = ({
  suit,
  rank,
  isFaceDown = false,
  elevation = 0,
  enableScale = false,
  isEmptyCard = false,
  width = 24
}: CardPropType) => {
  const { scale: globalScale } = useContext(ScaleContext);
  const scale = enableScale ? globalScale : 1;
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const prevFaceDown = useRef(isFaceDown);

  useEffect(() => {
    const front = frontRef.current;
    const back = backRef.current;
    if (!front || !back) return;

    if (prevFaceDown.current === isFaceDown) {
      gsap.set(front, { scaleX: isFaceDown ? 0 : 1 });
      gsap.set(back, { scaleX: isFaceDown ? 1 : 0 });
      return;
    }

    prevFaceDown.current = isFaceDown;

    const tl = gsap.timeline();
    if (isFaceDown) {
      tl.to(front, { scaleX: 0, duration: 0.2, ease: 'power2.in' }).to(back, {
        scaleX: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
    } else {
      tl.to(back, { scaleX: 0, duration: 0.2, ease: 'power2.in' }).to(front, {
        scaleX: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
    }

    return () => {
      tl.kill();
    };
  }, [isFaceDown]);

  const face = cardFaceSrc(rank, suit);
  const cardWidth = width * scale;
  const cornerRadius = cardCornerRadiusForWidth(cardWidth);

  const BaseStyle: CSSProperties = {
    position: 'relative',
    minWidth: cardWidth,
    width: cardWidth,
    height: cardHeightForWidth(cardWidth),
    border: isEmptyCard ? `1px dashed ${hiJackColors.white}` : `0px dashed ${hiJackColors.white}`,
    borderRadius: cornerRadius
  };

  return (
    <div style={BaseStyle}>
      {face && (
        <Paper
          ref={frontRef}
          elevation={elevation}
          style={{
            ...SideStyle,
            borderRadius: cornerRadius,
            transform: `scaleX(${isFaceDown ? 0 : 1})`
          }}>
          <img src={face} alt={cardAltText(rank, suit)} draggable={false} style={ArtStyle} />
        </Paper>
      )}
      {!isEmptyCard && (
        <Paper
          ref={backRef}
          elevation={elevation}
          style={{
            ...SideStyle,
            borderRadius: cornerRadius,
            transform: `scaleX(${isFaceDown ? 1 : 0})`
          }}>
          <img src={CARD_BACK_SRC} alt={CARD_BACK_ALT} draggable={false} style={ArtStyle} />
        </Paper>
      )}
    </div>
  );
};

export default XSmallCard;

// One side of the card — the artwork paints the face (or back) including its
// white fill and rounded corners; the Paper only carries the caller's elevation.
const SideStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  background: 'transparent'
};

const ArtStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  userSelect: 'none'
};
